import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePermissions } from '../../../../user/permissions';
import { ModerationAttendee, ModerationHistory, ModerationOrder, AttendeeModerationTransitions } from '../../../../models';

import { AnchorNav, EmptyCard, WeezHeader, Icon, Button, ButtonDropdown, DropdownItem, ToastContext, Status, SplitContainer } from '@weezevent/nacre';
import ImageEmptyCard from '../../../../images/img_page_error.png';
import { colors } from '../../../../constants/moderation';
import useDateFormat from '../../../../components/utils/useDateFormat';

import { ModerationProfileLeftContainer } from './LeftContainer';
import { ModerationProfileRightContainer } from './RightContainer';
import ModerationModal from './moderationModal';
import SendMailWithStatusModal from '../../components/sendMailWithStatusModal';
import LoadingCard from '../../../../components/loadingCard';
import { Loader } from 'semantic-ui-react';

import Style from './moderationProfile.module.css';

export default function ModerationProfile({ organization, attendeeId, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [openMailStatusModal, setOpenMailStatusModal] = React.useState(false);
    const [openModerationModal, setOpenModerationModal] = React.useState(false);
    const [isModerated, setIsModerated] = React.useState(false);
    const [attendee, setAttendee] = React.useState(null);
    const [moderationLock, setModerationLock] = React.useState(null);
    const permissions = usePermissions();
    const isAdmin = permissions.isModerationAdmin(organization.id);
    const [nextAttendeeLoading, setNextAttendeeLoading] = React.useState(false);

    const [, loadingAttendee, syncAttendee] = ModerationAttendee.useApiModel(
        {
            id: attendeeId,
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            flowId,
            query: {
                include: 'form.field'
            },
            onSuccess: response => {
                setAttendee(response);
            },
            onError: ({ response: { attendee, ...rest }, code }) => {
                if (code === 409) {
                    setIsModerated(true);
                    setAttendee(new ModerationAttendee(attendee));
                    setModerationLock(rest);
                }
            }
        },
        [organization, attendeeId]
    );

    const isAttendeeReady = React.useMemo(() => {
        return Boolean(attendee?.id && !isModerated && !loadingAttendee);
    }, [attendee?.id, isModerated, loadingAttendee]);

    const [order, loadingOrder, syncOrder] = ModerationOrder.useApiModel(
        {
            id: attendee?.order,
            launch: isAttendeeReady,
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false
        },
        [organization, attendee, flowId, isAttendeeReady]
    );

    const [historyDatas, loadingHistoryDatas, syncHistoryDatas] = ModerationHistory.useApiModel(
        {
            attendeeId: attendeeId,
            launch: attendeeId && !isModerated,
            flowId,
            organizationId: organization.id,
            cache: false,
            allow_cache: false
        },
        [attendeeId, organization, isModerated]
    );

    const [transitions, loadingTransitions, syncTransitions] = AttendeeModerationTransitions.useApiModel(
        {
            organizationId: organization.id,
            attendeeId: attendeeId,
            flowId,
            launch: Boolean(attendee),
            cache: false,
            allow_cache: false
        },
        [organization, flowId, attendeeId, attendee]
    );

    const lockId = React.useMemo(() => {
        if (!isModerated) {
            return attendee?.lock_id;
        }
        return moderationLock?.id;
    }, [isModerated, attendee, moderationLock]);

    const handleNextAttendee = React.useCallback(() => {
        setNextAttendeeLoading(true);

        ModerationAttendee.getNextAttendee({
            organizationId: organization.id,
            flowId,
            skip: true,
            query: window.location.search
        })
            .then(response => {
                if (response.id === parseInt(attendeeId)) {
                    syncAttendee();
                    syncHistoryDatas();
                    syncTransitions();
                } else {
                    history.push(response.url);
                }
                setIsModerated(false);
                setModerationLock(null);
                setOpenModerationModal(false);
            })
            .catch(() => {
                toast.error(t('ticket.moderation.attendee.nexAttendeeError'));
            })
            .finally(e => {
                setNextAttendeeLoading(false);
            });
    }, [organization, flowId, attendeeId, syncAttendee, syncHistoryDatas, syncTransitions]);

    const handleUnlockAttendee = React.useCallback(() => {
        ModerationAttendee.unlock({
            organizationId: organization.id,
            flowId,
            lockId
        })
            .then(({ status }) => {
                if (status === 204) {
                    toast.success(t('ticket.moderation.attendee.lock.toast.success'));
                    if (!flowId) {
                        history.push(`/ticket/O${organization.pk}/moderation/moderation_attendees`);
                    } else {
                        history.push(`/ticket/O${organization.pk}/moderation/moderation_flows/${flowId}/moderation_attendees`);
                    }
                }
            })
            .catch(() => {
                toast.error(t('ticket.moderation.attendee.lock.toast.not-found'));
            });
    }, [organization, flowId, lockId]);

    const handleDownloadTicket = React.useCallback(() => {
        window.open(attendee?.ticket_url, '_blank');
    }, [attendee]);

    const prefix = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organization.id}/moderation/moderation_attendees`;
        }
        return `/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/moderation_attendees`;
    }, [organization, flowId, attendeeId]);

    const loadingProfile = React.useMemo(() => loadingAttendee || loadingOrder || loadingHistoryDatas || loadingTransitions, [
        loadingAttendee,
        loadingOrder,
        loadingHistoryDatas,
        loadingTransitions
    ]);

    return (
        <>
            <WeezHeader
                title={
                    <div className={Style['attendee-header-label-container']}>
                        <Icon name="user" margin={false} />
                        <span>{attendee?.attendeeResolvedName}</span>
                        <Status color={colors[attendee?.status.stage]} text={attendee?.status.name} />
                    </div>
                }
                backLink={{
                    onClick: () => {
                        history.push(prefix);
                    }
                }}
                rightComponent={[
                    <div key="buttons" className={Style['attendee-header-ctas-container']}>
                        <Button
                            primary
                            icon={<Icon name="check-shield" />}
                            label={t('ticket.moderation.attendee.header.ctaModerate')}
                            className={Style['attendee-header-ctaModerate']}
                            disabled={isModerated || transitions?.length === 0}
                            onClick={() => setOpenModerationModal(true)}
                        />
                        <ButtonDropdown
                            renderWhenClosed
                            trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                            items={
                                <>
                                    {/*@TODO Uncomment when the feature is ready*/}
                                    {/*<DropdownItem*/}
                                    {/*    item={t('ticket.moderation.attendee.header.actionSendMail')}*/}
                                    {/*    onClick={() => setOpenMailStatusModal(true)}*/}
                                    {/*    disabled={isModerated}*/}
                                    {/*/>*/}
                                    <DropdownItem item={t('ticket.moderation.attendee.header.downloadTicket')} disabled={!attendee?.ticket_url} onClick={handleDownloadTicket} />
                                    <DropdownItem
                                        item={t('ticket.moderation.attendee.lock.unlock')}
                                        disabled={!isAdmin || !attendee?.is_lock_owner}
                                        onClick={handleUnlockAttendee}
                                    />
                                </>
                            }
                            className={Style['attendee-header-ctaActions']}
                        />
                        <div className={Style['switch-item-block']}>
                            <Button
                                icon={<Icon name="chevron-right" />}
                                label={nextAttendeeLoading ? <Loader className={Style['loader-count-loader']} active /> : t('ticket.moderation.attendee.header.ctaNextAttendee')}
                                onClick={() => handleNextAttendee()}
                            />
                        </div>
                    </div>
                ]}
            >
                <span className={Style['attendee-header-subtitle']}>
                    <span>{attendee?.moderation_flow_name} &gt;</span> {attendee?.event_name}
                </span>
                <SendMailWithStatusModal open={openMailStatusModal} setOpenModal={setOpenMailStatusModal} />
            </WeezHeader>
            {attendee && openModerationModal && (
                <ModerationModal
                    open={openModerationModal}
                    setOpenModal={setOpenModerationModal}
                    attendee={attendee}
                    organization={organization}
                    flowId={flowId}
                    handleNextAttendee={handleNextAttendee}
                    nextAttendeeLoading={nextAttendeeLoading}
                    transitions={transitions}
                    loadingTransitions={loadingTransitions}
                    order={order}
                />
            )}
            {!isModerated ? (
                <LoadingCard loading={loadingProfile}>
                    <AnchorNav
                        rootId="main"
                        items={[
                            {
                                id: 'general',
                                title: t('ticket.moderation.attendee.navGeneral')
                            },
                            {
                                id: 'requester',
                                title: t('ticket.moderation.attendee.navRequester')
                            },
                            {
                                id: 'attendee',
                                title: t('ticket.moderation.attendee.navAttendee')
                            },
                            {
                                id: 'history',
                                title: t('ticket.moderation.attendee.navHistory')
                            }
                        ]}
                    >
                        <SplitContainer additional={<ModerationProfileRightContainer prefix={prefix} attendee={attendee} order={order} organization={organization} />}>
                            <ModerationProfileLeftContainer
                                attendee={attendee}
                                order={order}
                                organization={organization}
                                flowId={flowId}
                                historyDatas={historyDatas}
                                loadingHistoryDatas={loadingHistoryDatas}
                                syncOrder={syncOrder}
                                syncHistoryDatas={syncHistoryDatas}
                                syncAttendee={syncAttendee}
                            />
                        </SplitContainer>
                    </AnchorNav>
                </LoadingCard>
            ) : (
                moderationLock && (
                    <OngoingModeration isAdmin={isAdmin} attendee={attendee} moderationLock={moderationLock} handleNext={handleNextAttendee} handleUnlock={handleUnlockAttendee} />
                )
            )}
        </>
    );
}

const OngoingModeration = ({ isAdmin, attendee, moderationLock, handleNext, handleUnlock }) => {
    const { t } = useTranslation();
    const name = `${moderationLock?.operator.first_name} ${moderationLock?.operator.last_name}`;
    const [, time, date] = useDateFormat({
        date: moderationLock?.creation_date,
        tz: attendee?.event_timezone,
        dateStyle: 'short',
        timeStyle: 'short'
    });

    return (
        <EmptyCard
            title={t('ticket.moderation.attendee.lock.title')}
            subTitle={t('ticket.moderation.attendee.lock.subtitle')}
            image={ImageEmptyCard}
            cta={
                <div className={Style['attendee-moderation-lock-card']}>
                    <Button.Group>
                        <Button primary label={t('ticket.moderation.attendee.lock.next')} onClick={handleNext} />
                        {isAdmin && <Button primary inverted label={t('ticket.moderation.attendee.lock.unlock')} onClick={handleUnlock} />}
                    </Button.Group>
                    <span className={Style['attendee-moderation-lock-operator']}>{t('ticket.moderation.attendee.lock.operator', { name, date: `${date} ${time}` })}</span>
                </div>
            }
        />
    );
};
